import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import type { PluginBlock, PluginBlockFileContent, PluginsResponse } from '@shared/blockly/core/plugin-types';
import { extractBlockIdsFromPluginTxt, getPluginName, getRawBlocks, parseBlockFileToObject, registerPluginByTxt } from '@shared/blockly/plugins/plugin-parser';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { BlocklyDbType } from '@shared/index';
import type * as Blockly from 'blockly';
import { firstValueFrom } from 'rxjs';
import { BlocklyHelperService } from './shared/components/blockly/blockly-editor/blockly-helper.service';
import { RouteShareService } from './shared/services/route-share.service';

export type { PluginBlock, PluginBlockFileContent, PluginsResponse };

type ToolboxItemInfo = Blockly.utils.toolbox.ToolboxItemInfo;


@Injectable({
  providedIn: 'root'
})
export class BlocklyPluginsService {
  public pluginIdToNameMap: Map<string, string>;
  public pluginIdToBlockIdsMap: Map<string, string[]>;
  constructor(private http: HttpClient, private rss: RouteShareService, private BH: BlocklyHelperService, private toastr: ToastrService) {
    this.pluginIdToNameMap = new Map();
    this.pluginIdToBlockIdsMap = new Map();
  }

  public pluginUsage(pluginId: string, stages: BlocklyDbType[]) {
    let counter = 0;
    stages.forEach(stage => {
      const blockIds = this.pluginIdToBlockIdsMap.get(pluginId);
      blockIds?.forEach(blockId => {
        if (stage.xml.includes(blockId)) {
          counter++;
        }
      });


    });

    //@ts-ignore
    return counter;

  }

  public pluginIsValid(pluginTXT: string) {
    if (pluginTXT.trim() == "") {
      this.toastr.error('GENERAL', $localize`:@@empty-plugin-code:Bitte gib einen Plugin-Code ein.`, { timeOut: 10000 });
      return false;
    }

    if (!pluginTXT.startsWith("@plugin:")) {
      this.toastr.error('GENERAL', $localize`:@@plugin-code-must-start-with:Der Plugin-Code muss mit '@plugin:' beginnen.`, { timeOut: 10000 });
      return false;
    }


    return true;
  }





  public setPluginIdToName(pluginId: string, pluginName: string): void {
    this.pluginIdToNameMap.set(pluginId, pluginName);
  }

  public getPluginNameById(pluginId: string): string | undefined {
    return this.pluginIdToNameMap.get(pluginId);
  }


  public async removePluginById(pluginId: string): Promise<void> {
    await firstValueFrom(this.http.post(`/api/admin/plugin/remove/${this.rss.botConfig._id}/${pluginId}`, {}));
    this.pluginIdToNameMap.delete(pluginId);
    this.pluginIdToBlockIdsMap.delete(pluginId);
  }

  public async addPlugin(pluginTXT: string, pluginName: string): Promise<void> {
    const botId = this.rss.botConfig._id;
    const addCallback = await firstValueFrom(this.http.post(`/api/admin/plugin/add/${botId}`, pluginTXT));
    //@ts-ignore
    this.setPluginIdToName(addCallback["_id"], pluginName);
    //@ts-ignore
    this.pluginIdToBlockIdsMap.set(addCallback["_id"], this.extractBlockIdsFromPluginTxt(pluginTXT));
  }

  public async updatePlugin(pluginId: string, pluginTXT: string, pluginName: string): Promise<void> {
    const botId = this.rss.botConfig._id;
    await firstValueFrom(this.http.post(`/api/admin/plugin/update/${botId}/${pluginId}`, pluginTXT));
    this.setPluginIdToName(pluginId, pluginName);
    this.pluginIdToBlockIdsMap.set(pluginId, this.extractBlockIdsFromPluginTxt(pluginTXT));
  }

  public extractBlockIdsFromPluginTxt(pluginTXT: string): string[] {
    return extractBlockIdsFromPluginTxt(pluginTXT);
  }

  public pluginNameisTaken(pluginName: string, excludePluginId?: string | null): boolean {
    for (const [id, name] of this.pluginIdToNameMap.entries()) {
      if (id === excludePluginId) continue;
      if (name === pluginName) {
        return true;
      }
    }
    return false;
  }

  public getPluginName(pluginTXT: string, excludePluginId?: string | null): string {
    return getPluginName(pluginTXT, pluginName => this.pluginNameisTaken(pluginName, excludePluginId));
  }


  public getRawBlocks(pluginTXT: string): string[] {
    return getRawBlocks(pluginTXT);
  }

  public async getPlugins() {
    const botId = this.rss.botConfig._id;
    const r = await firstValueFrom(this.http.get<PluginsResponse>(`/api/admin/plugin/get/plugins/${botId}`));
    this.pluginIdToNameMap.clear();
    this.pluginIdToBlockIdsMap.clear();

    return r;
  }

  public addBlockUniqueIdsToPluginTxt(pluginTXT: string): string {
    // 1. Den Text in einzelne Blöcke splitten
    const blocks = pluginTXT.split('###BLOCKDEF').filter(block => block.trim() !== "");

    const processedBlocks = blocks.map((block, index) => {
      let cleanBlock = block.trim();

      // Prüfung: Existiert bereits eine ID?
      // Sucht nach dem Muster #::ID gefolgt von der nächsten Zeile
      const hasId = /^#::ID/m.test(cleanBlock);

      if (!hasId) {
        // Generiere eine neue ID (z.B. basierend auf dem Index oder Zeitstempel)
        const generatedId = `GENERATED_ID_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

        // Füge die ID am Anfang des Blocks ein
        cleanBlock = `#::ID \n${generatedId}\n${cleanBlock}`;
      }

      return `###BLOCKDEF\n${cleanBlock}`;
    });

    // 2. Wieder zu einem Gesamtstring zusammenfügen
    const fixed = processedBlocks.join('\n\n');
    const parts = fixed.split("@plugin:");
    return "@plugin:" + parts[1];


  }


  // MAKE SHURE THAT THE PLUGIN TXT HAS UNIQUE IDS FOR ALL BLOCKS,
  // MAKE SHURE THAT THE PLUGIN TXT HAS UNIQUE IDS FOR ALL BLOCKS,
  // MAKE SHURE THAT THE PLUGIN TXT HAS UNIQUE IDS FOR ALL BLOCKS,
  // USE addBlockUniqueIdsToPluginTxt BEFORE
  public registerPluginByTxt(fixedPluginTXT: string, excludePluginId?: string | null): ToolboxItemInfo | null {
    return registerPluginByTxt(this.BH, fixedPluginTXT, {
      force: true,
      pluginNameIsTaken: pluginName => this.pluginNameisTaken(pluginName, excludePluginId),
      onMissingBlockId: blockText => {
        this.toastr.error('GENERAL', $localize`:@@plugin-block-missing-id:Ein Block in diesem Plugin hat keine ID. Bitte füge eine Zeile mit '#::ID' hinzu, um diesem Block eine eindeutige ID zu geben. Block-Text: ${blockText}`);
      },
    });
  }

  public async fetchPluginByPath(pluginId: string) {
    // const org = this.getCurrentOrg();
    const botId = this.rss.botConfig._id;
    const url = `/api/admin/plugin/get/pluginfile/${botId}/${pluginId}`;
    return firstValueFrom(this.http.get(url, { responseType: 'text' }));
  }

  public parseBlockFileToObject(text: string): PluginBlockFileContent {
    return parseBlockFileToObject(text);
  }


}
